import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import AddPalletDialog from './add-pallet-dialog';
import Footer from './footer';
import { cn } from "@/lib/utils";
import { RootState } from '@/redux/store';
import { setActivePallet, addPalletType, PalletType as PalletSliceType } from '@/redux/slices/pallet/palletSlice';
import { useCreatePalletMutation, useGetPalletsQuery } from '@/redux/slices/pallet/palletApiSlice';
import LpnTable from './LpnTable';
import { showErrorToast, showSuccessToast } from "./toast/toast";
import { setPalletTypes } from '@/redux/slices/pallet/palletSlice';
import { createdPalletFromResponse } from '@/lib/pallets';
import { useNavigate, useLocation } from 'react-router-dom';
import { Skeleton } from "@/components/ui/skeleton";
import { ChevronsLeft, ChevronsRight } from 'lucide-react';
import { Button } from './ui/button';
import { showWarningToast } from './toast/toast';
interface LpnNoProps {
  projectId?: string;
  isOffline?: boolean;
  onOfflineSubmit?: (values: any) => Promise<void>;
  getLpnNumbers?: () => Promise<any>;
}

type PalletType = Omit<PalletSliceType, 'lpnNumbers'>;

export default function LpnNo({ isOffline, onOfflineSubmit, getLpnNumbers }: LpnNoProps) {
  const pathname = useLocation();
  const projectId = useSelector((state: RootState) => state.define);
  const dispatch = useDispatch();
  const { palletTypes, activePallet } = useSelector((state: RootState) => state.pallets);
  const [dataSaving, setDataSaving] = useState(false);
  const { data: pallets, isFetching, refetch: refetchPallets } = useGetPalletsQuery(projectId ?? '', {
    skip: !projectId || isOffline,
  });
  const [isNav, setIsNav] = useState(true);
  const [isLoadingLpnData, setIsLoadingLpnData] = useState(false);
  const [createPallet] = useCreatePalletMutation();
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  useEffect(() => {
    if (pallets && !isOffline) {
      dispatch(setPalletTypes(pallets.pallets));
    }
  }, [pallets, dispatch, isOffline]);

  // Create a component for the LPN table skeleton
  const LpnTableSkeleton = () => (
    <div className="space-y-6 w-full">
      <div className="flex justify-between items-center">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-10 w-32" />
      </div>

      <div className="border rounded-md overflow-hidden">
        <div className="p-4 bg-gray-50 border-b">
          <div className="flex space-x-4">
            <Skeleton className="h-6 w-24" />
            <Skeleton className="h-6 w-24" />
            <Skeleton className="h-6 w-24" />
          </div>
        </div>

        <div className="p-4">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="grid grid-cols-4 gap-4 py-3 border-b last:border-0">
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-8 w-full" />
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-end">
        <Skeleton className="h-10 w-32" />
      </div>
    </div>
  );

  console.log(projectId, 'projectId')
  const handleAddPalletDialog = useCallback(async (palletData: any) => {
    if (isOffline && onOfflineSubmit) {
      await onOfflineSubmit({
        type: 'addPallet',
        data: palletData
      });
      return;
    }

    try {
      const response = await createPallet({
        type: "create",
        palletDetails: {
          variant: palletData.variant,
          name: palletData.name,
          numberOfPallets: palletData.numberOfPallets,
          projectId: palletData.projectId
        },
        shippingModeIds: palletData.shippingModes,
      }).unwrap();
      const createdPallet = createdPalletFromResponse(response);
      if (createdPallet) {
        dispatch(addPalletType({ ...createdPallet, lpnNumbers: [] }));
      }
      showSuccessToast("New Pallet Added and Generated");
      refetchPallets();
    } catch (error) {
      showErrorToast("Failed to add pallet");
      console.error("Error adding pallet:", error);
    }
  }, [isOffline, onOfflineSubmit, dispatch, createPallet, refetchPallets]);

  const saveLpnNumbers = useCallback(async () => {
    if (activePallet && !isOffline) {
      const lpnTable = document.getElementById(`lpn-table-${activePallet.id}`);
      if (lpnTable) {
        const submitButton = lpnTable.querySelector('button[type="submit"]') as HTMLButtonElement | null;
        if (submitButton) {
          await submitButton.click();
        }
      }
    }
    setHasUnsavedChanges(false);
  }, [activePallet]);

  const handleTabChange = async (value: string) => {
    setIsLoadingLpnData(true);
    if (hasUnsavedChanges && !isOffline) {
      await saveLpnNumbers();
    }
    const selectedPallet = palletTypes.find(pallet => pallet.id?.toString() === value);
    if (selectedPallet) {
      dispatch(setActivePallet(selectedPallet));
    }
    // Short timeout to simulate loading and ensure UI updates
    setTimeout(() => {
      setIsLoadingLpnData(false);
    }, 400);
  };
  const handleNextPallet = () => {
    console.log('next pallet')
    const index = palletTypes.findIndex(p => p.id === Number(activePallet?.id));
    console.log(index, 'index of pallet')
    if (index < palletTypes.length - 1) {
      handleTabChange(palletTypes[index + 1].id?.toString() || "")
    } else {
      showWarningToast('No more pallets')
    }
  }
  const handlePreviousPallet = () => {
    console.log('previous pallet')
    const index = palletTypes.findIndex(p => p.id === Number(activePallet?.id));
    console.log(index, 'index of pallet')
    if (index > 0) {
      handleTabChange(palletTypes[index - 1].id?.toString() || "")
    } else {
      showWarningToast('No more pallets')
    }
  }
  const handleFormChange = useCallback(() => {
    setHasUnsavedChanges(true);
  }, []);

  if (isFetching) {
    return (
      <div className="w-full flex h-[calc(100vh-140px)]">
        {/* Sidebar Skeleton */}
        <div className="w-[170px] h-full bg-light-blue flex flex-col items-start justify-start pt-10">
          <Skeleton className="h-6 w-36 mx-5 my-3" />
          <div className="w-full px-5 space-y-4 mt-4">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-16 w-full rounded-md" />
            ))}
          </div>
          <div className="px-5 mt-5 w-full">
            <Skeleton className="h-10 w-full rounded-md" />
          </div>
        </div>

        {/* Main Content Skeleton */}
        <div className="flex-1 p-8">
          <LpnTableSkeleton />
        </div>
      </div>
    );
  }

  return (
    <div className="w-full flex h-[calc(100vh-140px)]">
      <Tabs
        defaultValue={activePallet?.id?.toString() ?? ''}
        onValueChange={handleTabChange}
        value={activePallet?.id?.toString() ?? ''}
        className="w-full flex"
      >

        {

          isNav?
          <TabsList className="w-[170px] h-full bg-light-blue flex flex-col items-start justify-start pt-10 overflow-y-auto relative">
          <ChevronsLeft onClick={() => setIsNav(!isNav)} className="cursor-pointer absolute top-2 right-2 text-blue" />
          <p className="p-5 text-md text-black text-left font-semibold">Added Pallets</p>
          {palletTypes.map((pallet) => (
            <TabsTrigger
              key={pallet.id}
              value={pallet.id?.toString() ?? ''}
              className="w-full justify-start px-5 py-4 text-left border-b-0 last:border-b-0"
            >
              <div className="flex flex-col gap-1">
                <div>{pallet.variant}</div>
                <div>{pallet.name}</div>
              </div>
            </TabsTrigger>
          ))}
          {/* {!pathname.pathname.includes('offline') && <AddPalletDialog
            onAddPallet={handleAddPalletDialog}
            createPalletLoading={false}
            isOffline={isOffline}
          />} */}
        </TabsList>:
        <TabsList className="w-[40px] relative z-50 h-full bg-light-blue flex flex-col items-start justify-start pt-10 overflow-y-auto">
        <ChevronsRight onClick={() => setIsNav(!isNav)} className="cursor-pointer absolute top-2 right-2 text-blue" />
      </TabsList>
        }
        <div className="flex-1 overflow-hidden ">
          {palletTypes.map((pallet) => (
            <TabsContent
              key={pallet.id}
              value={pallet.id?.toString() ?? ''}
              className={cn(
                "h-full overflow-y-auto p-8 pb-[100px]",
                "space-y-4"
              )}
            >
              {activePallet?.id === pallet.id && isLoadingLpnData ? (
                <LpnTableSkeleton />
              ) : (
                <LpnTable
                  pallet={pallet}
                  setDataSaving={setDataSaving}
                  projectId={projectId}
                  onFormChange={handleFormChange}
                  getLpnNumbers={getLpnNumbers}
                  isOffline={isOffline}
                  onOfflineSubmit={async (values) => {
                    if (isOffline && onOfflineSubmit) {
                      await onOfflineSubmit({
                        type: 'lpnNumbers',
                        palletId: pallet.id,
                        data: values
                      });
                    }
                  }}
                />
              )}
               <div  className="w-full  flex-row flex items-center justify-end gap-2 ">
                    {activePallet?.id !== palletTypes[0]?.id && <Button onClick={() => handlePreviousPallet()} className="  bg-blue hover:bg-[#00336C]">
                      Previous pallet
                    </Button> }
                    {activePallet?.id !== palletTypes[palletTypes.length - 1]?.id && <Button onClick={() => handleNextPallet()} className="  bg-blue hover:bg-[#00336C]">
                      Next pallet
                    </Button>}
                  </div>
            </TabsContent>
            
          ))}
          
        </div>
        
      </Tabs>
      
      {!isOffline && <Footer
        layout
        isLoading={dataSaving}
        onSubmit={saveLpnNumbers}
        hasUnsavedChanges={true}
      />}

    </div>
  );
}
